import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { tap } from 'rxjs/operators';
import { DialogEditMode } from '../../../shared/models/dialog-edit-mode.enum';
import { AuthStoreService } from '../../../shared/services/auth/auth-store.service';
import { TagForm } from '../../forms/tag.form';
import { CustomerBiller } from '../../models/model';
import { SubAccountWithBillerData } from '../../models/sub-account.model';
import { TagListDialogCloseAction } from '../../models/tag-list-dialog-close-action.enum';
import { BaseTag, Tag } from '../../models/tag.model';
import { TagService } from '../../services/tag.service';

@Component({
standalone: false,
  selector: 'app-tag-list-dialog',
  templateUrl: './tag-list-dialog.component.html',
})
export class TagListDialogComponent implements OnInit {

  public tags: Tag[];

  public parentName: string;
  public customerBillerId: string;
  public subAccountId: string;
  public closeAction = TagListDialogCloseAction.CLOSE;

  public editMode = DialogEditMode;
  public mode = DialogEditMode.CREATE;

  public form = new TagForm();
  public editForm = new TagForm();

  public filteredTags: Tag[];
  public editedTag: Tag;

  private customerId: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public input: any,
    private tagService: TagService,
    private authStoreService: AuthStoreService,
  ) {
  }

  public ngOnInit(): void {
    this.customerId = this.authStoreService.customerId;

    if (this.input.biller) {
      this.setBillerParams(this.input);
      this.getBillerTags();
    } else {
      this.setSubAccountParams(this.input);
      this.getSubAccountTags();
    }

    this.form.tagName.valueChanges.subscribe((query: string) => {
      this.filteredTags = query ? this.search(query) : [];
    });
  }

  public addCustomTag = (name: string) => {
    this.tagService
      .addCustomTag(this.customerId, name)
      .pipe(
        tap((tag: BaseTag) => {
          this.form.clearTagName();
          this.checkTagChange({ ...tag, checked: false });
          this.tags.push({ ...tag, checked: true });
        }),
      )
      .subscribe();
  };

  public editClicked = (tag: Tag) => {
    this.editedTag = tag;
    this.editForm.setTagName(tag.name);
    this.switchToEditeMode();
  };

  public checkTagChange = (tag: Tag) => {
    if (!tag.checked) {
      this.subAccountId ? this.assignToSubAccount(tag) : this.assignToCustomerBiller(tag);
    } else {
      this.subAccountId ? this.deleteTagFromSubAccount(tag.id) : this.deleteTagFromCustomerBiller(tag.id);
    }
    this.closeActionSave();
  };

  public updateTag = (tag: Tag, tagName: string) => this.tagService.updateTag(this.customerId, tag.id, tagName).pipe(
    tap((newTag: BaseTag) => {
      this.tags.find((t) => t.id === tag.id).name = newTag.name;
      this.switchToCreateMode();
      this.closeActionSave();
    }),
  );

  public delete = (tag: Tag) => this.tagService.deleteTag(this.customerId, tag).pipe(
    tap(() => {
      this.removeTagFromList(tag);
      this.switchToCreateMode();
      this.closeActionSave();
    }),
  );

  public cancelEditing = () => {
    this.editForm.clearTagName();
    this.switchToCreateMode();
  };

  public cancelAdding = () => {
    this.form.clearTagName();
  };

  private getBillerTags(): void {
    this.tagService.getCustomerBillerTags(this.customerBillerId).subscribe((tags) => (this.tags = tags));
  }

  private getSubAccountTags(): void {
    this.tagService.getSubAccountTags(this.subAccountId, this.customerBillerId).subscribe((tags) => (this.tags = tags));
  }

  private setBillerParams(customerBiller: CustomerBiller): void {
    this.parentName = customerBiller.biller.name;
    this.customerBillerId = customerBiller.id;
  }

  private setSubAccountParams(subAccount: SubAccountWithBillerData): void {
    this.parentName = `${subAccount.customerBillerName} ${subAccount.name}`;
    this.subAccountId = subAccount.id;
    this.customerBillerId = subAccount.customerBillerId;
  }

  private search(query: string): Tag[] {
    return [...this.tags.filter((t) => t.name.toLocaleLowerCase().includes(query.toLocaleLowerCase()))];
  }

  private assignToSubAccount(tag: Tag): void {
    let action;
    if (tag.id === null) {
      action = this.tagService.assignNewTagToSubAccount(this.customerBillerId, this.subAccountId, tag.name);
    } else {
      action = this.tagService.assignToSubAccount(this.customerBillerId, this.subAccountId, tag.id);
    }

    action.subscribe(() => this.getSubAccountTags());
  }

  private deleteTagFromSubAccount(tagId: string): void {
    this.tagService
      .deleteTagFromSubAccount(this.customerBillerId, this.subAccountId, tagId)
      .subscribe(() => this.getSubAccountTags());
  }

  private assignToCustomerBiller(tag: Tag): void {
    let action;
    if (tag.id === null) {
      action = this.tagService.assignNewTagToCustomerBiller(this.customerBillerId, tag.name);
    } else {
      action = this.tagService.assignToCustomerBiller(this.customerBillerId, tag.id);
    }

    action.subscribe(() => this.getBillerTags());
  }

  private deleteTagFromCustomerBiller(tagId: string): void {
    this.tagService.deleteTagFromCustomerBiller(this.customerBillerId, tagId).subscribe(() => this.getBillerTags());
  }

  private switchToCreateMode(): void {
    this.mode = DialogEditMode.CREATE;
  }

  private switchToEditeMode(): void {
    this.mode = DialogEditMode.EDIT;
  }

  private removeTagFromList(tag: Tag): void {
    const index = this.tags.indexOf(tag);
    if (index >= 0) {
      this.tags.splice(index, 1);
    }
  }

  private closeActionSave(): void {
    this.closeAction = TagListDialogCloseAction.SAVE;
  }
}
